import Head from 'next/head';
import Link from 'next/link';

import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { ReactComponent as Chevron } from '@pflegia/ui/icons/chevron.svg';
import { removeTrailingSlash } from '@pflegia/util/common/remove-trailing-slash';

import { getBaseUrlByCurrentSourceDomain } from '@utils/domain';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  // Using jsonIdHref for BreadcrumbJsonId for cases where the href is not navigateable directly.
  jsonIdHref?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  jsonLdId?: string;
  jsonLdName?: string;
  showBreadcrumb?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, jsonLdId, jsonLdName, showBreadcrumb = true }) => {
  const { sourceDomain } = useFlavorFromRoute();
  const baseUrl = sourceDomain
    ? getBaseUrlByCurrentSourceDomain(process.env.NEXT_PUBLIC_BASE_URL, sourceDomain)
    : process.env.NEXT_PUBLIC_BASE_URL || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    ...(jsonLdId
      ? {
          '@id': jsonLdId,
        }
      : {}),
    '@type': 'BreadcrumbList',
    ...(jsonLdName
      ? {
          name: jsonLdName,
        }
      : {}),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: removeTrailingSlash(`${baseUrl}${item.href ?? item.jsonIdHref ?? ''}`),
    })),
  };

  return (
    <>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replaceAll('<', String.raw`\u003c`) }}
        />
      </Head>

      {showBreadcrumb && (
        <nav aria-label='breadcrumb'>
          <ol className='flex flex-wrap items-center gap-x-1 gap-y-2 text-black'>
            {items.map((item, index) => (
              <li key={item.label} className='breadcrumb-item flex items-center'>
                {item.href ? (
                  <Link href={item.href}>
                    <span className='text-primary'>{item.label}</span>
                  </Link>
                ) : (
                  <span>{item.label}</span>
                )}
                {index < items.length - 1 && (
                  <span className='mx-px'>
                    <Chevron />
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
    </>
  );
};

export default Breadcrumb;
